import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import './PaymentInfoForm.css';

interface PaymentInfoFormProps {
  collaborationId: string;
  email: string;
  agreedPrice: number;
  handle: string;
  platform: string;
  onComplete: () => void;
  existingPaymentInfo?: any;
}

interface TaxFormData {
  formType?: 'W9' | 'W8-BEN';
  taxId: string;
  businessType?: string;
  dateOfBirth?: string;
  foreignCountry?: string;
}

const PaymentInfoForm: React.FC<PaymentInfoFormProps> = ({
  collaborationId,
  email,
  agreedPrice,
  handle,
  platform,
  onComplete,
  existingPaymentInfo
}) => {
  // Initialize step based on existing data
  const [step, setStep] = useState<'info' | 'tax' | 'invoice'>(() => {
    // If we have existing payment info with all required fields
    if (existingPaymentInfo?.firstName && existingPaymentInfo?.lastName &&
        existingPaymentInfo?.country && existingPaymentInfo?.address &&
        existingPaymentInfo?.city && existingPaymentInfo?.stateProvince &&
        existingPaymentInfo?.postalCode) {
      // If tax info is required and not yet provided, go to tax step
      if (agreedPrice >= 600 && !existingPaymentInfo?.taxInfo?.dateOfBirth && !existingPaymentInfo?.taxInfo?.taxId) {
        return 'tax';
      }
      // Otherwise go to invoice step
      return 'invoice';
    }
    return 'info';
  });
  const [submitting, setSubmitting] = useState(false);
  const [copiedContent, setCopiedContent] = useState(false);
  const [invoiceLink, setInvoiceLink] = useState('');
  
  // Personal Info State - Initialize with existing data if available
  const [personalInfo, setPersonalInfo] = useState({
    firstName: existingPaymentInfo?.firstName || '',
    lastName: existingPaymentInfo?.lastName || '',
    country: existingPaymentInfo?.country || '',
    address: existingPaymentInfo?.address || '',
    city: existingPaymentInfo?.city || '',
    stateProvince: existingPaymentInfo?.stateProvince || '',
    postalCode: existingPaymentInfo?.postalCode || ''
  });

  // Tax Info State - Initialize with existing data if available
  const [taxInfo, setTaxInfo] = useState<TaxFormData>({
    taxId: existingPaymentInfo?.taxInfo?.taxId || '',
    businessType: existingPaymentInfo?.taxInfo?.businessType || 'individual',
    dateOfBirth: existingPaymentInfo?.taxInfo?.dateOfBirth || '',
    foreignCountry: existingPaymentInfo?.taxInfo?.foreignCountry || ''
  });

  const requiresTaxInfo = agreedPrice >= 600;

  // Determine tax form type based on country
  useEffect(() => {
    if (personalInfo.country) {
      setTaxInfo(prev => ({
        ...prev,
        formType: personalInfo.country === 'US' ? 'W9' : 'W8-BEN',
        foreignCountry: personalInfo.country !== 'US' ? personalInfo.country : ''
      }));
    }
  }, [personalInfo.country]);

  const countries = [
    { code: 'US', name: 'United States', flag: '🇺🇸' },
    { code: 'AF', name: 'Afghanistan', flag: '🇦🇫' },
    { code: 'AL', name: 'Albania', flag: '🇦🇱' },
    { code: 'DZ', name: 'Algeria', flag: '🇩🇿' },
    { code: 'AD', name: 'Andorra', flag: '🇦🇩' },
    { code: 'AO', name: 'Angola', flag: '🇦🇴' },
    { code: 'AG', name: 'Antigua and Barbuda', flag: '🇦🇬' },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
    { code: 'AM', name: 'Armenia', flag: '🇦🇲' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺' },
    { code: 'AT', name: 'Austria', flag: '🇦🇹' },
    { code: 'AZ', name: 'Azerbaijan', flag: '🇦🇿' },
    { code: 'BS', name: 'Bahamas', flag: '🇧🇸' },
    { code: 'BH', name: 'Bahrain', flag: '🇧🇭' },
    { code: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
    { code: 'BB', name: 'Barbados', flag: '🇧🇧' },
    { code: 'BY', name: 'Belarus', flag: '🇧🇾' },
    { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
    { code: 'BZ', name: 'Belize', flag: '🇧🇿' },
    { code: 'BJ', name: 'Benin', flag: '🇧🇯' },
    { code: 'BT', name: 'Bhutan', flag: '🇧🇹' },
    { code: 'BO', name: 'Bolivia', flag: '🇧🇴' },
    { code: 'BA', name: 'Bosnia and Herzegovina', flag: '🇧🇦' },
    { code: 'BW', name: 'Botswana', flag: '🇧🇼' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
    { code: 'BN', name: 'Brunei', flag: '🇧🇳' },
    { code: 'BG', name: 'Bulgaria', flag: '🇧🇬' },
    { code: 'BF', name: 'Burkina Faso', flag: '🇧🇫' },
    { code: 'BI', name: 'Burundi', flag: '🇧🇮' },
    { code: 'CV', name: 'Cabo Verde', flag: '🇨🇻' },
    { code: 'KH', name: 'Cambodia', flag: '🇰🇭' },
    { code: 'CM', name: 'Cameroon', flag: '🇨🇲' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦' },
    { code: 'CF', name: 'Central African Republic', flag: '🇨🇫' },
    { code: 'TD', name: 'Chad', flag: '🇹🇩' },
    { code: 'CL', name: 'Chile', flag: '🇨🇱' },
    { code: 'CN', name: 'China', flag: '🇨🇳' },
    { code: 'CO', name: 'Colombia', flag: '🇨🇴' },
    { code: 'KM', name: 'Comoros', flag: '🇰🇲' },
    { code: 'CG', name: 'Congo', flag: '🇨🇬' },
    { code: 'CR', name: 'Costa Rica', flag: '🇨🇷' },
    { code: 'HR', name: 'Croatia', flag: '🇭🇷' },
    { code: 'CU', name: 'Cuba', flag: '🇨🇺' },
    { code: 'CY', name: 'Cyprus', flag: '🇨🇾' },
    { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿' },
    { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
    { code: 'DJ', name: 'Djibouti', flag: '🇩🇯' },
    { code: 'DM', name: 'Dominica', flag: '🇩🇲' },
    { code: 'DO', name: 'Dominican Republic', flag: '🇩🇴' },
    { code: 'EC', name: 'Ecuador', flag: '🇪🇨' },
    { code: 'EG', name: 'Egypt', flag: '🇪🇬' },
    { code: 'SV', name: 'El Salvador', flag: '🇸🇻' },
    { code: 'GQ', name: 'Equatorial Guinea', flag: '🇬🇶' },
    { code: 'ER', name: 'Eritrea', flag: '🇪🇷' },
    { code: 'EE', name: 'Estonia', flag: '🇪🇪' },
    { code: 'SZ', name: 'Eswatini', flag: '🇸🇿' },
    { code: 'ET', name: 'Ethiopia', flag: '🇪🇹' },
    { code: 'FJ', name: 'Fiji', flag: '🇫🇯' },
    { code: 'FI', name: 'Finland', flag: '🇫🇮' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'GA', name: 'Gabon', flag: '🇬🇦' },
    { code: 'GM', name: 'Gambia', flag: '🇬🇲' },
    { code: 'GE', name: 'Georgia', flag: '🇬🇪' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪' },
    { code: 'GH', name: 'Ghana', flag: '🇬🇭' },
    { code: 'GR', name: 'Greece', flag: '🇬🇷' },
    { code: 'GD', name: 'Grenada', flag: '🇬🇩' },
    { code: 'GT', name: 'Guatemala', flag: '🇬🇹' },
    { code: 'GN', name: 'Guinea', flag: '🇬🇳' },
    { code: 'GW', name: 'Guinea-Bissau', flag: '🇬🇼' },
    { code: 'GY', name: 'Guyana', flag: '🇬🇾' },
    { code: 'HT', name: 'Haiti', flag: '🇭🇹' },
    { code: 'HN', name: 'Honduras', flag: '🇭🇳' },
    { code: 'HU', name: 'Hungary', flag: '🇭🇺' },
    { code: 'IS', name: 'Iceland', flag: '🇮🇸' },
    { code: 'IN', name: 'India', flag: '🇮🇳' },
    { code: 'ID', name: 'Indonesia', flag: '🇮🇩' },
    { code: 'IR', name: 'Iran', flag: '🇮🇷' },
    { code: 'IQ', name: 'Iraq', flag: '🇮🇶' },
    { code: 'IE', name: 'Ireland', flag: '🇮🇪' },
    { code: 'IL', name: 'Israel', flag: '🇮🇱' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹' },
    { code: 'JM', name: 'Jamaica', flag: '🇯🇲' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵' },
    { code: 'JO', name: 'Jordan', flag: '🇯🇴' },
    { code: 'KZ', name: 'Kazakhstan', flag: '🇰🇿' },
    { code: 'KE', name: 'Kenya', flag: '🇰🇪' },
    { code: 'KI', name: 'Kiribati', flag: '🇰🇮' },
    { code: 'KP', name: 'North Korea', flag: '🇰🇵' },
    { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
    { code: 'KW', name: 'Kuwait', flag: '🇰🇼' },
    { code: 'KG', name: 'Kyrgyzstan', flag: '🇰🇬' },
    { code: 'LA', name: 'Laos', flag: '🇱🇦' },
    { code: 'LV', name: 'Latvia', flag: '🇱🇻' },
    { code: 'LB', name: 'Lebanon', flag: '🇱🇧' },
    { code: 'LS', name: 'Lesotho', flag: '🇱🇸' },
    { code: 'LR', name: 'Liberia', flag: '🇱🇷' },
    { code: 'LY', name: 'Libya', flag: '🇱🇾' },
    { code: 'LI', name: 'Liechtenstein', flag: '🇱🇮' },
    { code: 'LT', name: 'Lithuania', flag: '🇱🇹' },
    { code: 'LU', name: 'Luxembourg', flag: '🇱🇺' },
    { code: 'MG', name: 'Madagascar', flag: '🇲🇬' },
    { code: 'MW', name: 'Malawi', flag: '🇲🇼' },
    { code: 'MY', name: 'Malaysia', flag: '🇲🇾' },
    { code: 'MV', name: 'Maldives', flag: '🇲🇻' },
    { code: 'ML', name: 'Mali', flag: '🇲🇱' },
    { code: 'MT', name: 'Malta', flag: '🇲🇹' },
    { code: 'MH', name: 'Marshall Islands', flag: '🇲🇭' },
    { code: 'MR', name: 'Mauritania', flag: '🇲🇷' },
    { code: 'MU', name: 'Mauritius', flag: '🇲🇺' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
    { code: 'FM', name: 'Micronesia', flag: '🇫🇲' },
    { code: 'MD', name: 'Moldova', flag: '🇲🇩' },
    { code: 'MC', name: 'Monaco', flag: '🇲🇨' },
    { code: 'MN', name: 'Mongolia', flag: '🇲🇳' },
    { code: 'ME', name: 'Montenegro', flag: '🇲🇪' },
    { code: 'MA', name: 'Morocco', flag: '🇲🇦' },
    { code: 'MZ', name: 'Mozambique', flag: '🇲🇿' },
    { code: 'MM', name: 'Myanmar', flag: '🇲🇲' },
    { code: 'NA', name: 'Namibia', flag: '🇳🇦' },
    { code: 'NR', name: 'Nauru', flag: '🇳🇷' },
    { code: 'NP', name: 'Nepal', flag: '🇳🇵' },
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
    { code: 'NI', name: 'Nicaragua', flag: '🇳🇮' },
    { code: 'NE', name: 'Niger', flag: '🇳🇪' },
    { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
    { code: 'MK', name: 'North Macedonia', flag: '🇲🇰' },
    { code: 'NO', name: 'Norway', flag: '🇳🇴' },
    { code: 'OM', name: 'Oman', flag: '🇴🇲' },
    { code: 'PK', name: 'Pakistan', flag: '🇵🇰' },
    { code: 'PW', name: 'Palau', flag: '🇵🇼' },
    { code: 'PS', name: 'Palestine', flag: '🇵🇸' },
    { code: 'PA', name: 'Panama', flag: '🇵🇦' },
    { code: 'PG', name: 'Papua New Guinea', flag: '🇵🇬' },
    { code: 'PY', name: 'Paraguay', flag: '🇵🇾' },
    { code: 'PE', name: 'Peru', flag: '🇵🇪' },
    { code: 'PH', name: 'Philippines', flag: '🇵🇭' },
    { code: 'PL', name: 'Poland', flag: '🇵🇱' },
    { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
    { code: 'QA', name: 'Qatar', flag: '🇶🇦' },
    { code: 'RO', name: 'Romania', flag: '🇷🇴' },
    { code: 'RU', name: 'Russia', flag: '🇷🇺' },
    { code: 'RW', name: 'Rwanda', flag: '🇷🇼' },
    { code: 'KN', name: 'Saint Kitts and Nevis', flag: '🇰🇳' },
    { code: 'LC', name: 'Saint Lucia', flag: '🇱🇨' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines', flag: '🇻🇨' },
    { code: 'WS', name: 'Samoa', flag: '🇼🇸' },
    { code: 'SM', name: 'San Marino', flag: '🇸🇲' },
    { code: 'ST', name: 'Sao Tome and Principe', flag: '🇸🇹' },
    { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
    { code: 'SN', name: 'Senegal', flag: '🇸🇳' },
    { code: 'RS', name: 'Serbia', flag: '🇷🇸' },
    { code: 'SC', name: 'Seychelles', flag: '🇸🇨' },
    { code: 'SL', name: 'Sierra Leone', flag: '🇸🇱' },
    { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
    { code: 'SK', name: 'Slovakia', flag: '🇸🇰' },
    { code: 'SI', name: 'Slovenia', flag: '🇸🇮' },
    { code: 'SB', name: 'Solomon Islands', flag: '🇸🇧' },
    { code: 'SO', name: 'Somalia', flag: '🇸🇴' },
    { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
    { code: 'SS', name: 'South Sudan', flag: '🇸🇸' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸' },
    { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰' },
    { code: 'SD', name: 'Sudan', flag: '🇸🇩' },
    { code: 'SR', name: 'Suriname', flag: '🇸🇷' },
    { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
    { code: 'CH', name: 'Switzerland', flag: '🇨🇭' },
    { code: 'SY', name: 'Syria', flag: '🇸🇾' },
    { code: 'TW', name: 'Taiwan', flag: '🇹🇼' },
    { code: 'TJ', name: 'Tajikistan', flag: '🇹🇯' },
    { code: 'TZ', name: 'Tanzania', flag: '🇹🇿' },
    { code: 'TH', name: 'Thailand', flag: '🇹🇭' },
    { code: 'TL', name: 'Timor-Leste', flag: '🇹🇱' },
    { code: 'TG', name: 'Togo', flag: '🇹🇬' },
    { code: 'TO', name: 'Tonga', flag: '🇹🇴' },
    { code: 'TT', name: 'Trinidad and Tobago', flag: '🇹🇹' },
    { code: 'TN', name: 'Tunisia', flag: '🇹🇳' },
    { code: 'TR', name: 'Turkey', flag: '🇹🇷' },
    { code: 'TM', name: 'Turkmenistan', flag: '🇹🇲' },
    { code: 'TV', name: 'Tuvalu', flag: '🇹🇻' },
    { code: 'UG', name: 'Uganda', flag: '🇺🇬' },
    { code: 'UA', name: 'Ukraine', flag: '🇺🇦' },
    { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪' },
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'UY', name: 'Uruguay', flag: '🇺🇾' },
    { code: 'UZ', name: 'Uzbekistan', flag: '🇺🇿' },
    { code: 'VU', name: 'Vanuatu', flag: '🇻🇺' },
    { code: 'VA', name: 'Vatican City', flag: '🇻🇦' },
    { code: 'VE', name: 'Venezuela', flag: '🇻🇪' },
    { code: 'VN', name: 'Vietnam', flag: '🇻🇳' },
    { code: 'YE', name: 'Yemen', flag: '🇾🇪' },
    { code: 'ZM', name: 'Zambia', flag: '🇿🇲' },
    { code: 'ZW', name: 'Zimbabwe', flag: '🇿🇼' }
  ];

  const getCollaborationDetails = () => {
    return `Collaboration Details:
Platform: ${platform}
Creator: @${handle}
Agreed Price: $${agreedPrice} USD
Email: ${email}`;
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(getCollaborationDetails());
    setCopiedContent(true);
    toast.success('✅ Collaboration details copied to clipboard!');
    setTimeout(() => setCopiedContent(false), 3000);
  };

  // Save personal info when moving to next step
  const handleContinueFromPersonalInfo = async () => {
    // Validate all required fields
    if (!personalInfo.firstName || !personalInfo.lastName || !personalInfo.country || 
        !personalInfo.address || !personalInfo.city || !personalInfo.stateProvince || !personalInfo.postalCode) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      // Save personal info to database (without invoiceLink, so stage won't change)
      const response = await fetch(`${API_URL}/api/collaborations/public/${collaborationId}/payment-info`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          personalInfo: {
            ...personalInfo
          }
          // invoiceLink를 보내지 않음으로써 stage가 변경되지 않음
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save personal information');
      }

      // Move to next step based on amount
      if (requiresTaxInfo) {
        setStep('tax');
        toast.success('✅ Personal information saved. Please provide tax information.');
      } else {
        setStep('invoice');
        toast.success('✅ Personal information saved');
      }
    } catch (error) {
      console.error('Error saving personal info:', error);
      toast.error('Failed to save personal information');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    // Validate all required fields
    if (!personalInfo.firstName || !personalInfo.lastName || !personalInfo.country || 
        !personalInfo.address || !personalInfo.city || !personalInfo.stateProvince || !personalInfo.postalCode) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate tax info if required
    if (requiresTaxInfo && step === 'tax') {
      if (taxInfo.formType === 'W9' && !taxInfo.taxId) {
        toast.error('Tax ID is required for W-9 form');
        return;
      }
      if (taxInfo.formType === 'W8-BEN' && !taxInfo.dateOfBirth) {
        toast.error('Date of birth is required for W-8BEN form');
        return;
      }
    }

    // Validate PayPal invoice link
    if (!invoiceLink) {
      toast.error('Please enter your PayPal invoice link');
      return;
    }

    // Check if the invoice link is a valid PayPal URL
    if (!invoiceLink.includes('paypal.com')) {
      toast.error('Please enter a valid PayPal invoice link');
      return;
    }

    // Check if user accidentally pasted collaboration details instead of invoice link
    if (invoiceLink.includes('Collaboration Details:') || invoiceLink.includes('Platform:')) {
      toast.error('You entered the collaboration details instead of the PayPal invoice link. Please enter the actual PayPal invoice URL.');
      return;
    }

    setSubmitting(true);
    try {
      // Submit payment info with invoiceLink (this will change stage to processing_payment)
      const paymentData: any = {
        personalInfo: {
          ...personalInfo
        },
        invoiceLink: invoiceLink // invoiceLink가 있으므로 stage가 processing_payment로 변경됨
      };

      // Add tax info if required
      if (requiresTaxInfo) {
        if (taxInfo.formType === 'W9' && taxInfo.taxId) {
          paymentData.taxInfo = {
            type: taxInfo.formType,
            businessType: taxInfo.businessType,
            taxId: taxInfo.taxId,
            dateOfBirth: taxInfo.dateOfBirth,
            foreignCountry: taxInfo.foreignCountry
          };
        } else if (taxInfo.formType === 'W8-BEN' && taxInfo.dateOfBirth) {
          paymentData.taxInfo = {
            type: taxInfo.formType,
            businessType: taxInfo.businessType,
            dateOfBirth: taxInfo.dateOfBirth,
            foreignCountry: taxInfo.foreignCountry
          };
        }
      }

      const paymentResponse = await fetch(`${API_URL}/api/collaborations/public/${collaborationId}/payment-info`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData)
      });

      if (!paymentResponse.ok) {
        throw new Error('Failed to submit payment information');
      }

      // Submit tax info if required
      if (requiresTaxInfo) {
        const taxData: any = {
          type: taxInfo.formType,
          businessType: taxInfo.businessType,
          firstName: personalInfo.firstName,
          lastName: personalInfo.lastName,
          address: personalInfo.address,
          city: personalInfo.city,
          state: personalInfo.stateProvince,
          postalCode: personalInfo.postalCode,
          country: personalInfo.country,
          dateOfBirth: taxInfo.dateOfBirth
        };

        // Only add taxId for W9
        if (taxInfo.formType === 'W9' && taxInfo.taxId) {
          taxData.taxId = taxInfo.taxId;
        }

        const taxResponse = await fetch(`${API_URL}/api/tax-info/submit/${collaborationId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(taxData)
        });

        if (!taxResponse.ok) {
          console.error('Failed to submit tax information');
        }
      }

      toast.success('✅ Payment information submitted successfully!');
      onComplete();
    } catch (error) {
      console.error('Error submitting payment info:', error);
      toast.error('Failed to submit payment information');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch(step) {
      case 'info':
        return (
          <div className="payment-form-step">
            <h3>Personal Information</h3>
            <p className="step-description">Please provide your personal information for payment processing. This information is required for IRS tax reporting purposes.</p>
            
            <div className="form-row">
              <div className="form-group">
                <label>First Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={personalInfo.firstName}
                  onChange={(e) => setPersonalInfo({...personalInfo, firstName: e.target.value})}
                  placeholder="Enter your first name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={personalInfo.lastName}
                  onChange={(e) => setPersonalInfo({...personalInfo, lastName: e.target.value})}
                  placeholder="Enter your last name"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Country <span className="required">*</span></label>
              <select
                value={personalInfo.country}
                onChange={(e) => setPersonalInfo({...personalInfo, country: e.target.value})}
                required
              >
                <option value="">Select your country</option>
                {countries.map(country => (
                  <option key={country.code} value={country.code}>{country.flag} {country.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Address <span className="required">*</span></label>
              <input
                type="text"
                value={personalInfo.address}
                onChange={(e) => setPersonalInfo({...personalInfo, address: e.target.value})}
                placeholder="Street address"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City <span className="required">*</span></label>
                <input
                  type="text"
                  value={personalInfo.city}
                  onChange={(e) => setPersonalInfo({...personalInfo, city: e.target.value})}
                  placeholder="City"
                  required
                />
              </div>
              <div className="form-group">
                <label>State/Province <span className="required">*</span></label>
                <input
                  type="text"
                  value={personalInfo.stateProvince}
                  onChange={(e) => setPersonalInfo({...personalInfo, stateProvince: e.target.value})}
                  placeholder="State or Province"
                  required
                />
              </div>
              <div className="form-group">
                <label>Postal Code <span className="required">*</span></label>
                <input
                  type="text"
                  value={personalInfo.postalCode}
                  onChange={(e) => setPersonalInfo({...personalInfo, postalCode: e.target.value})}
                  placeholder="Postal code"
                  required
                />
              </div>
            </div>

            <div className="form-actions">
              <button 
                className="btn-primary"
                onClick={handleContinueFromPersonalInfo}
                disabled={
                  submitting ||
                  !personalInfo.firstName || 
                  !personalInfo.lastName || 
                  !personalInfo.country || 
                  !personalInfo.address ||
                  !personalInfo.city ||
                  !personalInfo.stateProvince ||
                  !personalInfo.postalCode
                }
              >
                {submitting ? 'Saving...' : requiresTaxInfo ? 'Continue to Tax Information' : 'Continue to Invoice Instructions'}
              </button>
            </div>
          </div>
        );

      case 'invoice':
        return (
          <div className="payment-form-step">
            <h3>📧 PayPal Invoice Instructions</h3>
            <div className="invoice-guide">
              <div className="invoice-amount">
                <span className="label">Amount to Invoice:</span>
                <span className="amount">${agreedPrice} USD</span>
              </div>
              
              <div className="invoice-recipient">
                <span className="label">Send Invoice to:</span>
                <span className="email">eric@ssemble.com</span>
              </div>

              <div className="invoice-steps">
                <h4>How to make your PayPal invoice:</h4>
                <ol>
                  <li>Log in to your PayPal account</li>
                  <li>Go to "Send & Request" → "Create Invoice"</li>
                  <li>Enter <strong>eric@ssemble.com</strong> as the recipient</li>
                  <li>Set the amount to <strong>${agreedPrice} USD</strong></li>
                  <li>In the description/notes section, paste the collaboration details below</li>
                  <li>Send the invoice</li>
                  <li>After sending, copy the invoice link from PayPal</li>
                  <li>Paste the invoice link in the field below</li>
                </ol>
              </div>

              <div className="collaboration-details">
                <h4>Collaboration Details to Include:</h4>
                <div className="details-box">
                  <pre>{getCollaborationDetails()}</pre>
                </div>
                <button 
                  className={`btn-copy ${copiedContent ? 'copied' : ''}`}
                  onClick={copyToClipboard}
                >
                  {copiedContent ? '✅ Copied!' : '📋 Copy Details'}
                </button>
              </div>

              <div className="important-note">
                <strong>⚠️ Important:</strong> Please send an invoice for the agreed amount to eric@ssemble.com with the above details. Make sure to include these collaboration details in your invoice description so we can match the payment to your collaboration.
              </div>

              <div className="form-group" style={{ marginTop: '20px' }}>
                <label>PayPal Invoice Link <span className="required">*</span></label>
                <input
                  type="url"
                  value={invoiceLink}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Prevent setting collaboration details as invoice link
                    if (value.includes('Collaboration Details:') || value.includes('Platform:')) {
                      toast.error('⚠️ Please enter the PayPal invoice URL, not the collaboration details');
                      return;
                    }
                    setInvoiceLink(value);
                  }}
                  placeholder="https://www.paypal.com/invoice/..."
                  pattern="https?://.*paypal\.com/.*"
                  title="Please enter a valid PayPal invoice URL"
                  required
                />
                <small>Enter the PayPal invoice link after you've sent it to eric@ssemble.com</small>
                {invoiceLink && !invoiceLink.includes('paypal.com') && (
                  <small style={{ color: 'red', display: 'block', marginTop: '5px' }}>
                    ⚠️ Please enter a valid PayPal invoice URL
                  </small>
                )}
              </div>

              <div className="form-actions">
                <button className="btn-secondary" onClick={() => requiresTaxInfo ? setStep('tax') : setStep('info')}>
                  Back
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleSubmit} 
                  disabled={submitting || !invoiceLink || !invoiceLink.includes('paypal.com') || invoiceLink.includes('Collaboration Details:')}
                >
                  {submitting ? 'Submitting...' : 'Complete'}
                </button>
              </div>
            </div>
          </div>
        );

      case 'tax':
        return (
          <div className="payment-form-step">
            <h3>📋 Tax Information Required</h3>
            <p className="tax-notice">
              Since your collaboration payment is $600 or more, we need to collect tax information for IRS reporting.
            </p>

            <div className="tax-form-type">
              <span className="label">Form Type:</span>
              <span className="form-type">
                {taxInfo.formType === 'W9' ? 'W-9 (U.S. Person)' : 'W-8BEN (Foreign Individual)'}
              </span>
            </div>

            {taxInfo.formType === 'W9' ? (
              <>
                <div className="form-group">
                  <label>Tax ID (SSN or EIN) <span className="required">*</span></label>
                  <input
                    type="text"
                    value={taxInfo.taxId}
                    onChange={(e) => setTaxInfo({...taxInfo, taxId: e.target.value})}
                    placeholder="XXX-XX-XXXX (SSN) or XX-XXXXXXX (EIN)"
                    required
                  />
                  <small>Your Social Security Number or Employer Identification Number</small>
                </div>

                <div className="form-group">
                  <label>Business Type</label>
                  <select
                    value={taxInfo.businessType}
                    onChange={(e) => setTaxInfo({...taxInfo, businessType: e.target.value})}
                  >
                    <option value="individual">Individual/Sole Proprietor</option>
                    <option value="corporation">Corporation</option>
                    <option value="partnership">Partnership</option>
                    <option value="llc">LLC</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label>Date of Birth <span className="required">*</span></label>
                  <input
                    type="date"
                    value={taxInfo.dateOfBirth}
                    onChange={(e) => setTaxInfo({...taxInfo, dateOfBirth: e.target.value})}
                    required
                  />
                </div>
              </>
            )}

            <div className="tax-disclaimer">
              <p>
                <strong>Privacy Notice:</strong> Your tax information is collected solely for IRS reporting 
                purposes and will be stored securely. This information will only be used to generate 
                required tax forms (1099-MISC or 1042-S) at year end.
              </p>
            </div>

            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setStep('info')}>
                Back
              </button>
              <button
                className="btn-primary"
                onClick={async () => {
                  // Validate tax info
                  if (taxInfo.formType === 'W9' && !taxInfo.taxId) {
                    toast.error('Tax ID is required');
                    return;
                  }
                  if (taxInfo.formType === 'W8-BEN' && !taxInfo.dateOfBirth) {
                    toast.error('Date of birth is required for W-8BEN form');
                    return;
                  }

                  // Save tax info to database
                  setSubmitting(true);
                  try {
                    const response = await fetch(`${API_URL}/api/collaborations/public/${collaborationId}/payment-info`, {
                      method: 'PATCH',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        taxInfo: {
                          type: taxInfo.formType,
                          businessType: taxInfo.businessType,
                          taxId: taxInfo.formType === 'W9' ? taxInfo.taxId : undefined,
                          dateOfBirth: taxInfo.dateOfBirth,
                          foreignCountry: taxInfo.foreignCountry
                        }
                      })
                    });

                    if (!response.ok) {
                      throw new Error('Failed to save tax information');
                    }

                    toast.success('✅ Tax information saved');
                    setStep('invoice');
                  } catch (error) {
                    console.error('Error saving tax info:', error);
                    toast.error('Failed to save tax information');
                  } finally {
                    setSubmitting(false);
                  }
                }}
                disabled={submitting || (taxInfo.formType === 'W9' && !taxInfo.taxId) || (taxInfo.formType === 'W8-BEN' && !taxInfo.dateOfBirth)}
              >
                {submitting ? 'Saving...' : 'Continue to Invoice Instructions'}
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="payment-info-form">
      <div className="form-progress">
        <div className={`progress-step ${step === 'info' ? 'active' : ''} ${(step === 'tax' || step === 'invoice') ? 'completed' : ''}`}>
          <span className="step-number">1</span>
          <span className="step-label">Personal Info</span>
        </div>
        {requiresTaxInfo && (
          <>
            <div className={`progress-line ${(step === 'tax' || step === 'invoice') ? 'completed' : ''}`}></div>
            <div className={`progress-step ${step === 'tax' ? 'active' : ''} ${step === 'invoice' ? 'completed' : ''}`}>
              <span className="step-number">2</span>
              <span className="step-label">Tax Info</span>
            </div>
          </>
        )}
        <div className={`progress-line ${step === 'invoice' ? 'completed' : ''}`}></div>
        <div className={`progress-step ${step === 'invoice' ? 'active' : ''}`}>
          <span className="step-number">{requiresTaxInfo ? '3' : '2'}</span>
          <span className="step-label">Invoice Guide</span>
        </div>
      </div>

      {renderStepContent()}
    </div>
  );
};

export default PaymentInfoForm;